'use client';

import { useState, useEffect, useRef } from 'react';
import { sanitizeHTML } from '@/lib/utils'

interface Testimonial {
  id: string;
  author: string;
  source: string;
  text: string;
  rating: number;
}

interface TestimonialsTabProps {
  testimonials: Testimonial[];
}

export default function TestimonialsTab({ testimonials }: TestimonialsTabProps) {
  // States for slider
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderTestimonials = testimonials.filter(t => t.source === 'Google').slice(0, 3);
  
  // States for filtering and sorting
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [visibleCount, setVisibleCount] = useState(4);
  
  // Refs for slide heights
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [sliderHeight, setSliderHeight] = useState<number | 'auto'>('auto');
  const containerRef = useRef<HTMLDivElement | null>(null);
  
  // Function to update height
  const updateHeight = () => {
    if (slideRefs.current[currentSlide]) {
      const slideHeight = slideRefs.current[currentSlide]?.scrollHeight;
      if (slideHeight) {
        // Add a small buffer to prevent any text cutoff
        setSliderHeight(slideHeight + 20);
      }
    }
  };
  
  // Update slider height when current slide changes
  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      updateHeight();
    }, 50);
    
    return () => clearTimeout(timer);
  }, [currentSlide]);
  
  // Update height on window resize
  useEffect(() => {
    const handleResize = () => {
      updateHeight();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [currentSlide]);

  // Filter testimonials from Vitals.com
  const vitalsTestimonials = testimonials
    .filter(t => t.source === 'Vitals.com')
    .sort((a, b) => {
      if (sortOrder === 'newest') {
        return parseInt(b.id) - parseInt(a.id);
      } else {
        return parseInt(a.id) - parseInt(b.id);
      }
    });

  // All other testimonials
  const otherTestimonials = testimonials
    .filter(t => t.source !== 'Vitals.com' && 
                !sliderTestimonials.some(st => st.id === t.id))
    .sort((a, b) => {
      if (sortOrder === 'newest') {
        return parseInt(b.id) - parseInt(a.id);
      } else {
        return parseInt(a.id) - parseInt(b.id);
      }
    });

  // Combined list of non-slider testimonials
  const combinedTestimonials = [...vitalsTestimonials, ...otherTestimonials];
  const displayedTestimonials = combinedTestimonials.slice(0, visibleCount);

  // Handler for "Load more" button
  const handleLoadMore = () => {
    setVisibleCount(prev => Math.min(prev + 4, combinedTestimonials.length));
  };

  // Handler for slider navigation with sliding animation
  const nextSlide = () => {
    setCurrentSlide((prev) => {
      const next = (prev + 1) % sliderTestimonials.length;
      return next;
    });
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => {
      const next = (prev - 1 + sliderTestimonials.length) % sliderTestimonials.length;
      return next;
    });
  };

  // Star rating component
  const StarRating = ({ rating }: { rating: number }) => {
    return (
      <div className="flex">
        {Array.from({ length: 5 }).map((_, index) => (
          <svg 
            key={index}
            className="w-7 h-7 text-brown"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  return (
		<>
			{/* Order controls */}
			<div className='TestimonialsPageOrder flex items-center justify-end gap-6 text-[1.125rem] mb-8'>
				<span className='text-neutral-900'>Order by:</span>
				<div className='flex items-center gap-4'>
					<button
						onClick={() => setSortOrder('newest')}
						className={`flex items-center gap-2 ${
							sortOrder === 'newest' ? 'font-medium' : 'text-neutral-400'
						} hover:font-medium transition-colors`}
					>
						<span>Newest</span>
						<svg
							width='14'
							height='14'
							viewBox='0 0 14 14'
							fill='none'
							xmlns='http://www.w3.org/2000/svg'
							className={`transform ${
								sortOrder === 'newest' ? 'text-neutral-600' : 'text-neutral-400'
							}`}
						>
							<path
								d='M7 2.33334V11.6667'
								stroke='currentColor'
								strokeWidth='1.5'
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
							<path
								d='M11.6666 7L6.99992 11.6667L2.33325 7'
								stroke='currentColor'
								strokeWidth='1.5'
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
						</svg>
					</button>
					<button
						onClick={() => setSortOrder('oldest')}
						className={`flex items-center gap-2 ${
							sortOrder === 'oldest' ? 'font-medium' : 'text-neutral-400'
						} hover:text-neutral-900 transition-colors`}
					>
						<span>Oldest</span>
						<svg
							width='14'
							height='14'
							viewBox='0 0 14 14'
							fill='none'
							xmlns='http://www.w3.org/2000/svg'
							className={`transform rotate-180 ${
								sortOrder === 'oldest' ? 'font-medium' : 'text-neutral-400'
							}`}
						>
							<path
								d='M7 2.33334V11.6667'
								stroke='currentColor'
								strokeWidth='1.5'
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
							<path
								d='M11.6666 7L6.99992 11.6667L2.33325 7'
								stroke='currentColor'
								strokeWidth='1.5'
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
						</svg>
					</button>
				</div>
			</div>

			{/* Slider section */}
			{sliderTestimonials.length > 0 && (
				<div className='bg-white rounded-lg shadow-[0px_4px_9px_0px_rgba(0,0,0,0.10),0px_16px_16px_0px_rgba(0,0,0,0.09),0px_35px_21px_0px_rgba(0,0,0,0.05),0px_63px_25px_0px_rgba(0,0,0,0.01),0px_98px_28px_0px_rgba(0,0,0,0.00)] p-6 px-8 md:px-24 md:p-12 mb-12 relative'>
					<button
						onClick={prevSlide}
						className='absolute left-1 md:left-10 top-1/2 -translate-y-1/2 p-2 z-10'
						aria-label='Previous slide'
					>
						<svg
							width='17'
							height='28'
							viewBox='0 0 17 28'
							fill='none'
							xmlns='http://www.w3.org/2000/svg'
						>
							<path
								d='M15 26L2 14L15 2'
								stroke='#0c1414'
								strokeWidth='3'
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
						</svg>
					</button>

					<div className='relative'>
						{sliderTestimonials.map((testimonial, index) => (
							<div
								key={testimonial.id}
								className={`transition-opacity duration-500 px-2 ${
									index === currentSlide
										? 'opacity-100 relative z-10'
										: 'opacity-0 absolute top-0 left-0 right-0'
								}`}
							>
								<div className='flex flex-col gap-5'>
									<span className='font-bold text-base font-brandon'>
										HIGHLIGHTED REVIEWS
									</span>
									<h3 className='font-brandon text-brown max-md:text-2xl'>
										{testimonial.author} (via {testimonial.source})
									</h3>
									<StarRating rating={testimonial.rating} />
									<p
										className='max-md:text-base whitespace-pre-wrap'
										dangerouslySetInnerHTML={sanitizeHTML(testimonial.text)}
									/>
								</div>
							</div>
						))}
					</div>

					<button
						onClick={nextSlide}
						className='absolute right-1 md:right-10 top-1/2 -translate-y-1/2 p-2 z-10'
						aria-label='Next slide'
					>
						<svg
							width='17'
							height='28'
							viewBox='0 0 17 28'
							fill='none'
							xmlns='http://www.w3.org/2000/svg'
						>
							<path
								d='M2 2L15 14L2 26'
								stroke='#0c1414'
								strokeWidth='3'
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
						</svg>
					</button>
				</div>
			)}

			{/* Testimonials grid */}
			<div className='grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12'>
				{displayedTestimonials.map(testimonial => (
					<div key={testimonial.id} className='flex flex-col gap-5'>
						{testimonial.author && (
							<h3 className='font-brandon text-brown max-md:text-2xl'>
								{testimonial.author} (via {testimonial.source})
							</h3>
						)}
						<StarRating rating={testimonial.rating} />
						<p
							className='max-md:text-base whitespace-pre-wrap'
							dangerouslySetInnerHTML={sanitizeHTML(testimonial.text)}
						/>
					</div>
				))}
			</div>

			{/* Load more button */}
			{visibleCount < combinedTestimonials.length && (
				<div className='mt-12 text-center'>
					<button
						onClick={handleLoadMore}
						className='w-full px-12 py-3 bg-brown-light rounded-lg hover:bg-brown text-neutral-50 transition-colors text-2xl font-brandon text-center max-w-[340px] mx-auto'
					>
						Load More
					</button>
				</div>
			)}
		</>
	)
} 